import { type TremendousProduct, USD_RE } from '@/features/wallet/api'
import { formatNumber } from '@/utils/formatNumber'

export type FormLimitsCents = { minCents: number; maxCents: number | undefined }

export const usdToCents = (s: string): number => {
  if (!USD_RE.test(s)) throw new Error(`usdToCents: not a USD amount: ${s}`)
  return Number(s.replace('.', ''))
}

// The form's own limits in integer cents. The minimum rounds to the nearest cent
// (1.15 * 100 is 114.999...); the maximum rounds down, via micro-units so that
// 128.14 stays 12814 instead of floor(12813.999...).
export const formLimitsToCents = (minPayout: number, maxAmountUsd: number | undefined): FormLimitsCents => ({
  minCents: Math.round(minPayout * 100),
  maxCents: maxAmountUsd == null ? undefined : Math.floor(Math.round(maxAmountUsd * 1e6) / 1e4)
})

export const clampBand = (
  minCents: number,
  maxCents: number,
  limits: FormLimitsCents
): { lowerCents: number; upperCents: number } => {
  const lowerCents = Math.max(minCents, limits.minCents)
  const upperCents = limits.maxCents === undefined ? maxCents : Math.min(maxCents, limits.maxCents)
  if (lowerCents > upperCents) return { lowerCents: minCents, upperCents: maxCents }
  return { lowerCents, upperCents }
}

export const formatUsdCents = (cents: number): string => `$${formatNumber(cents / 100, 2)}`

export const formatNative = (amount: string, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(amount))

export const describeAmounts = (
  product: TremendousProduct,
  limits: FormLimitsCents,
  locale: string
): { text: string; fixedOnly: boolean } => {
  if (product.kind === 'fixed') {
    const text = product.fixed
      .map((f) =>
        f.usd === null ? formatNative(f.amount, product.currency, locale) : `≈ ${formatUsdCents(usdToCents(f.usd))}`
      )
      .join(' · ')
    return { text, fixedOnly: true }
  }
  const { min, max, minUsd, maxUsd } = product.range
  if (minUsd === null || maxUsd === null) {
    return {
      text: `${formatNative(min, product.currency, locale)} – ${formatNative(max, product.currency, locale)}`,
      fixedOnly: false
    }
  }
  const { lowerCents, upperCents } = clampBand(usdToCents(minUsd), usdToCents(maxUsd), limits)
  const text =
    lowerCents === upperCents
      ? `≈ ${formatUsdCents(lowerCents)}`
      : `≈ ${formatUsdCents(lowerCents)} – ${formatUsdCents(upperCents)}`
  return { text, fixedOnly: false }
}
